import React, { useEffect, useState } from "react";
import { CalendarClock, FileDown, Flag, ShieldAlert, AlertOctagon, TrendingUp, Wallet, Stamp } from "lucide-react";
import api from "@/lib/api";
import { Shell, Empty } from "@/components/Shell";
import { RagBadge } from "@/components/Rag";
import { useBranding } from "@/context/BrandingContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const money = (n) => (n || n === 0 ? n.toLocaleString(undefined, { maximumFractionDigits: 0 }) : "—");

const Card = ({ icon: Icon, label, value, hint, testId }) => (
  <div data-testid={testId} className="panel p-5">
    <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">
      <Icon size={13} /> {label}
    </div>
    <div className="mt-2 font-display text-3xl font-semibold text-slate-900">{value}</div>
    {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
  </div>
);

const Section = ({ icon: Icon, title, blurb, children, testId }) => (
  <section className="panel p-5" data-testid={testId}>
    <h3 className="flex items-center gap-2 font-display text-base font-semibold text-slate-800">
      <Icon size={16} className="text-slate-400" /> {title}
    </h3>
    {blurb && <p className="mt-1 text-xs text-slate-500">{blurb}</p>}
    <div className="mt-4">{children}</div>
  </section>
);

export default function ClientDashboard() {
  const { t } = useBranding();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/client/dashboard").then(({ data: d }) => setData(d)).catch(() => toast.error("Could not load your progress view"));
  }, []);

  const pack = async () => {
    try {
      const res = await api.get("/client/progress-pack.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "progress-pack.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not build the progress pack");
    }
  };

  const c = data?.counts;

  return (
    <Shell
      testId="client-dashboard-page"
      minimal
      title="Progress Overview"
      subtitle={t("A plain-language view of how your projects are tracking. Read only — nothing here can be changed.")}
      actions={
        <Button data-testid="client-progress-pack-btn" onClick={pack} className="gap-2" style={{ backgroundColor: "var(--brand-primary)" }}>
          <FileDown size={15} /> Progress Pack (PDF)
        </Button>
      }
    >
      {!data ? (
        <Empty testId="client-loading" text="Gathering your latest position…" />
      ) : data.counts.projects === 0 ? (
        <Empty testId="client-empty" text={t("No projects have been shared with you yet. Your PMO contact can grant access.")} />
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="panel p-5" data-testid="client-overall-rag">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Overall Status</div>
              <div className="mt-2 flex items-center gap-3">
                <RagBadge value={data.overall_rag} testId="client-overall-badge" />
                <span className="text-sm text-slate-600">
                  {data.project_rag.Red} red · {data.project_rag.Amber} amber · {data.project_rag.Green} green
                </span>
              </div>
              <div className="mt-2 text-xs text-slate-500">
                {data.overall_rag === "Green"
                  ? "Everything is tracking to plan."
                  : data.overall_rag === "Amber"
                    ? "Some areas need attention but are being managed."
                    : "One or more areas need a decision from you."}
              </div>
            </div>
            <Card icon={TrendingUp} label="Average Progress" value={`${c.average_percent_complete}%`} hint={`${c.projects} ${t("projects")} in scope`} testId="client-progress-card" />
            <Card icon={Flag} label="Milestones (90 days)" value={c.milestones_next_90_days} hint="Dates coming up next" testId="client-milestones-card" />
            <Card icon={ShieldAlert} label="Open Escalations" value={c.open_escalations} hint="Risks and issues raised to you" testId="client-escalations-card" />
          </div>

          <Section icon={TrendingUp} title={t("Your projects")} testId="client-projects-section">
            <div className="divide-y divide-slate-100">
              {data.projects.map((p) => (
                <div key={p.id} data-testid={`client-project-${p.id}`} className="flex flex-wrap items-center justify-between gap-3 py-3">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-slate-800">{p.name}</div>
                    <div className="text-xs text-slate-400">{p.stage || t("No stage")} · target {p.end_date || "—"}</div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-40">
                      <div className="mb-1 text-right text-xs text-slate-500">{p.percent_complete}%</div>
                      <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                        <div className="h-full rounded-full" style={{ width: `${p.percent_complete}%`, backgroundColor: "var(--brand-primary)" }} />
                      </div>
                    </div>
                    <RagBadge value={p.rag} testId={`client-project-rag-${p.id}`} />
                  </div>
                </div>
              ))}
            </div>
          </Section>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Section icon={CalendarClock} title="Key dates ahead" blurb="Milestones due in the next 90 days." testId="client-milestones-section">
              {data.milestones.length === 0 ? (
                <p className="text-sm text-slate-500">No milestones fall in the next 90 days.</p>
              ) : (
                <div className="divide-y divide-slate-100">
                  {data.milestones.map((m) => (
                    <div key={m.id} data-testid={`client-milestone-${m.id}`} className="flex items-center justify-between gap-3 py-2.5">
                      <div className="min-w-0">
                        <div className="truncate text-sm text-slate-800">{m.title}</div>
                        <div className="text-xs text-slate-400">{m.project_name}</div>
                      </div>
                      <div className="flex shrink-0 items-center gap-3">
                        <span className="text-xs text-slate-500">{m.date}</span>
                        <RagBadge value={m.rag} testId={`client-milestone-rag-${m.id}`} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            <Section icon={Stamp} title={t("Upcoming gates")} blurb={t("Where each project sits in its approval journey.")} testId="client-gates-section">
              {data.gates.length === 0 ? (
                <p className="text-sm text-slate-500">No gates recorded yet.</p>
              ) : (
                <div className="divide-y divide-slate-100">
                  {data.gates.map((g) => (
                    <div key={g.project_id} data-testid={`client-gate-${g.project_id}`} className="flex items-center justify-between gap-3 py-2.5">
                      <div className="min-w-0">
                        <div className="truncate text-sm text-slate-800">{g.project_name}</div>
                        <div className="text-xs text-slate-400">{g.framework || t("No framework")}</div>
                      </div>
                      <div className="shrink-0 text-right">
                        <div className="text-sm text-slate-700">{g.stage}</div>
                        <div className="text-xs text-slate-400">{g.gate_status}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Section icon={Wallet} title="Budget" blurb="Approved budget across your projects." testId="client-budget-section">
              <div className="mb-3 font-display text-2xl font-semibold text-slate-900" data-testid="client-total-budget">{money(data.budget.total_budget)}</div>
              <div className="divide-y divide-slate-100">
                {data.budget.by_project.map((b) => (
                  <div key={b.project_name} className="flex items-center justify-between gap-3 py-2 text-sm">
                    <span className="truncate text-slate-700">{b.project_name}</span>
                    <span className="shrink-0 text-slate-500">{money(b.budget)}</span>
                  </div>
                ))}
              </div>
            </Section>

            <Section icon={TrendingUp} title={t("Benefits")} blurb={t("What these projects are set to deliver.")} testId="client-benefits-section">
              <div className="mb-3 flex items-baseline gap-3">
                <span className="font-display text-2xl font-semibold text-slate-900" data-testid="client-benefits-value">{money(data.benefits.target_value)}</span>
                <span className="text-xs text-slate-500">{data.benefits.count} tracked · {data.benefits.realised} realised</span>
              </div>
              {data.benefits.top.length === 0 ? (
                <p className="text-sm text-slate-500">No benefits recorded yet.</p>
              ) : (
                <div className="divide-y divide-slate-100">
                  {data.benefits.top.map((b, i) => (
                    <div key={i} className="flex items-center justify-between gap-3 py-2 text-sm">
                      <div className="min-w-0">
                        <div className="truncate text-slate-700">{b.title}</div>
                        <div className="text-xs text-slate-400">{b.project_name} · {b.status}</div>
                      </div>
                      <span className="shrink-0 text-slate-500">{money(b.target_value)}</span>
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Section icon={ShieldAlert} title={t("Risks raised to you")} blurb={t("Things that could still go wrong, and what is being done.")} testId="client-risks-section">
              {data.escalated_risks.length === 0 ? (
                <p className="text-sm text-slate-500">Nothing escalated right now.</p>
              ) : (
                <div className="divide-y divide-slate-100">
                  {data.escalated_risks.map((r, i) => (
                    <div key={i} data-testid={`client-risk-${i}`} className="py-2.5">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-800">{r.title}</div>
                          <div className="text-xs text-slate-400">{r.project_name} · owner {r.owner || "unassigned"}</div>
                        </div>
                        <RagBadge value={r.rag} testId={`client-risk-rag-${i}`} />
                      </div>
                      {r.mitigation && <p className="mt-1 text-xs text-slate-600">Plan: {r.mitigation}</p>}
                    </div>
                  ))}
                </div>
              )}
            </Section>

            <Section icon={AlertOctagon} title={t("Issues being worked")} blurb={t("Problems happening now.")} testId="client-issues-section">
              {data.escalated_issues.length === 0 ? (
                <p className="text-sm text-slate-500">No open issues escalated.</p>
              ) : (
                <div className="divide-y divide-slate-100">
                  {data.escalated_issues.map((it, i) => (
                    <div key={i} data-testid={`client-issue-${i}`} className="py-2.5">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-800">{it.title}</div>
                          <div className="text-xs text-slate-400">{it.project_name} · owner {it.owner || "unassigned"}</div>
                        </div>
                        <span className="shrink-0 text-xs font-semibold text-slate-500">{it.priority}</span>
                      </div>
                      {it.resolution && <p className="mt-1 text-xs text-slate-600">Action: {it.resolution}</p>}
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </div>

          <Section icon={CalendarClock} title={t("Latest updates from the team")} testId="client-updates-section">
            {data.recent_updates.length === 0 ? (
              <p className="text-sm text-slate-500">No updates published yet.</p>
            ) : (
              <div className="space-y-4">
                {data.recent_updates.map((u) => (
                  <article key={u.project_id} data-testid={`client-update-${u.project_id}`} className="border-l-2 pl-4" style={{ borderColor: "var(--brand-primary)" }}>
                    <div className="flex flex-wrap items-center gap-2">
                      <h4 className="text-sm font-semibold text-slate-800">{u.project_name}</h4>
                      <RagBadge value={u.overall_rag} testId={`client-update-rag-${u.project_id}`} />
                      <span className="text-xs text-slate-400">period ending {u.period_ending || "—"} · {u.percent_complete ?? 0}% complete</span>
                    </div>
                    {u.exec_summary && <p className="mt-1.5 text-sm text-slate-700">{u.exec_summary}</p>}
                    {u.progress_this_period && <p className="mt-1 text-xs text-slate-600"><span className="font-semibold">Done this period:</span> {u.progress_this_period}</p>}
                    {u.upcoming_focus && <p className="mt-1 text-xs text-slate-600"><span className="font-semibold">Next:</span> {u.upcoming_focus}</p>}
                  </article>
                ))}
              </div>
            )}
          </Section>
        </div>
      )}
    </Shell>
  );
}
